import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card } from "../components/ui/card";
import {
  FileDown,
  Percent,
  TrendingDown,
  FileText,
  DollarSign,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_discount_reports,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";
import { formatDateString } from "../CommonJquery/CommonJquery";
import { retrieveData } from "../LocalConnection/LocalConnection";

export default function DiscountReport() {
  const { toast } = useToast();
  const restroName = retrieveData("restaurant_name");
  // Date states
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(new Date());

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [discountData, setDiscountData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats
  const [stats, setStats] = useState({
    totalDiscounts: 0,
    totalAmount: 0,
    percentageType: 0,
    flatType: 0,
  });

  // Fetch data
  const fetchDiscountData = useCallback(
    async (start_date, end_date, flag = "1", call_id = "0") => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", start_date.toISOString().split("T")[0]);
      fd.append("end_date", end_date.toISOString().split("T")[0]);
      fd.append("flag", flag);
      fd.append("call_id", call_id);

      try {
        const response = await server_post_data(get_discount_reports, fd);

        // console.log("Resp", response.data.message);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const data = response.data.message || [];

          // console.log("data", data);
          const message = response.data.message || {};
          const discountArray = Array.isArray(message.discount_data)
            ? message.discount_data
            : [];

          setDiscountData(discountArray);

          // Calculate stats
          if (discountArray.length !== 0) {
            const totalAmount = discountArray.reduce(
              (sum, item) => sum + parseFloat(item.total_discount || 0),
              0,
            );

            const percentageCount = discountArray.filter(
              (item) => item.discount_type === "Percentage",
            ).length;

            const flatCount = discountArray.filter(
              (item) => item.discount_type === "Flat",
            ).length;

            setStats({
              totalDiscounts: discountArray.length,
              totalAmount,
              percentageType: percentageCount,
              flatType: flatCount,
            });
          } else {
            setStats({
              totalDiscounts: 0,
              totalAmount: 0,
              percentageType: 0,
              flatType: 0,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching discount data:", error);
        toast({
          title: "Error",
          description: "Failed to load discount data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // Export to Excel
  const exportToExcel = useCallback(() => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

    // Table mein jo columns dikh rahe hain, waise hi map karo
    const exportData = discountData.map((row, index) => {
      const orderType = parseInt(row.order_type);

      const orderTypeLabel =
        orderType === 0 || orderType === 3
          ? "Dine-in"
          : orderType === 2
            ? "Delivery"
            : "Takeaway";

      const tableName = row.table_code_names
        ? ` (${row.table_code_names})`
        : "";

      return {
        "Sr.No": index + 1,
        "Restaurant Name": restroName || "-",
        "Created Date": formatDateString(row.entry_date) || "-",
        "Order No.": row.invoice_id || "-",

        "Order Type": orderTypeLabel + tableName,

        "Area Name": row.table_area_name || "-",
        "Biller Name": row.admin_name || "-",
        "Customer Name": row.guest_name || "-",
        "Customer Phone": row.guest_mobile_no || "-",
        Coupon: row.discount_code_name || "-",
        Reason: row.discount_reason || "-",

        "My Amount (₹)": parseFloat(row.product_amount || 0).toFixed(2),
        "Discount Amount (₹)": parseFloat(row.discount_amount || 0).toFixed(2),
        "Tax (₹)": parseFloat(row.tax_amount || 0).toFixed(2),
        "Round Off (₹)": parseFloat(row.roundoff_amount || 0).toFixed(2),
        "Grand Total (₹)": parseFloat(row.final_amount || 0).toFixed(2),
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Column widths set karo
    ws["!cols"] = [
      { wch: 6 }, // Sr.No
      { wch: 20 }, // Restaurant Name
      { wch: 15 }, // Created Date
      { wch: 12 }, // Order No.
      { wch: 12 }, // Order Type
      { wch: 15 }, // Area Name
      { wch: 15 }, // Biller Name
      { wch: 15 }, // Customer Name
      { wch: 14 }, // Customer Phone
      { wch: 12 }, // Coupon
      { wch: 20 }, // Reason
      { wch: 14 }, // My Amount
      { wch: 16 }, // Discount Amount
      { wch: 10 }, // Tax
      { wch: 10 }, // Round Off
      { wch: 14 }, // Grand Total
    ];

    // Header row bold karo
    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddr]) continue;
      ws[cellAddr].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F46E5" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    const wb = {
      Sheets: { "Discount Report": ws },
      SheetNames: ["Discount Report"],
    };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(
      data,
      `discount_report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    toast({
      title: "Success",
      description: "Report exported successfully",
    });
  }, [discountData, toast]);

  // Pagination
  const totalPages = Math.ceil(discountData.length / entriesPerPage);
  const currentData = discountData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Initial load
  useEffect(() => {
    fetchDiscountData(startDate, endDate, "1", "0");
  }, []);

  return (
    <MainLayout>
      <Header
        title="Discount Report"
        subtitle="View and analyze discount usage and distribution"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalDiscounts}
                </p>
                <p className="text-sm text-muted-foreground">Total Discounts</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalAmount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Amount</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.percentageType}
                </p>
                <p className="text-sm text-muted-foreground">Percentage Type</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.flatType}
                </p>
                <p className="text-sm text-muted-foreground">Flat Type</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="gap-2"
            disabled={discountData.length === 0}
          >
            <FileDown className="w-4 h-4" />
            Export Excel
          </Button>
        </div>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Sr.No</TableHead>
                <TableHead>Restaurant Name</TableHead>
                <TableHead>Created Date</TableHead>
                <TableHead>Order No.</TableHead>
                <TableHead>Order Type</TableHead>
                <TableHead>Area Name</TableHead>
                <TableHead>Biller Name</TableHead>
                <TableHead>Customer Name</TableHead>
                <TableHead>Customer Phone</TableHead>
                <TableHead>Coupon</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">My Amount</TableHead>
                <TableHead className="text-right">Discount</TableHead>
                <TableHead className="text-right">Tax</TableHead>
                <TableHead className="text-right">Round Off</TableHead>
                <TableHead className="text-right">Grand Total</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell colSpan={17} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={17} className="text-center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((row, index) => (
                  <TableRow key={index} className="border-border">
                    {/* Sr No */}
                    <TableCell>
                      {(currentPage - 1) * entriesPerPage + index + 1}
                    </TableCell>

                    {/* Restaurant Name */}
                    <TableCell>{restroName}</TableCell>

                    {/* Created Date */}
                    <TableCell>{formatDateString(row.entry_date)}</TableCell>

                    {/* Order No */}
                    <TableCell>{row.invoice_id}</TableCell>

                    {/* Order Type */}
                    <TableCell>
                      {parseInt(row.order_type) === 0 ||
                      parseInt(row.order_type) === 3
                        ? "Dine-in"
                        : parseInt(row.order_type) === 2
                          ? "Delivery"
                          : "Takeaway"}
                      {row.table_code_names ? ` (${row.table_code_names})` : ""}
                    </TableCell>

                    {/* Area Name */}
                    <TableCell>{row.table_area_name}</TableCell>

                    {/* Biller Name */}
                    <TableCell>{row.admin_name}</TableCell>

                    {/* Customer Name */}
                    <TableCell>{row.guest_name}</TableCell>

                    {/* Customer Phone */}
                    <TableCell>{row.guest_mobile_no}</TableCell>

                    {/* Coupon */}
                    <TableCell>{row.discount_code_name || "-"}</TableCell>

                    {/* Reason */}
                    <TableCell>{row.discount_reason}</TableCell>

                    {/* My Amount */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.product_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Discount Amount */}
                    <TableCell className="text-right text-destructive font-semibold">
                      ₹{parseFloat(row.discount_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Tax */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.tax_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Round Off */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.roundoff_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Grand Total */}
                    <TableCell className="text-right font-bold">
                      ₹{parseFloat(row.final_amount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to
                {Math.min(currentPage * entriesPerPage, discountData.length)} of
                {discountData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
